import { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRecipes, useSwipes, useFamilyMembers, useAllFeedback, useFavorites } from '../hooks/useSupabase';
import { RecipeThumb } from './RecipeImage';
import { MealIcon } from './MealIcon';
import { LoadingSpinner, EmptyState, ErrorState } from './ui/StateViews';
import Avatar from './Avatar';

function computeScores(recipes, swipes, feedback, memberIds) {
  const scores = {};
  recipes.forEach(r => { scores[r.id] = 1; });

  const idSet = new Set(memberIds);

  memberIds.forEach(mid => {
    const memberSwipes = swipes[mid] || {};
    Object.entries(memberSwipes).forEach(([rid, liked]) => {
      scores[rid] = (scores[rid] || 1) + (liked ? 3 : -2);
    });
  });

  (feedback || []).forEach(f => {
    if (!idSet.has(f.member_id)) return;
    if (f.stars >= 4) scores[f.recipe_id] = (scores[f.recipe_id] || 1) + 2;
    else if (f.stars <= 2) scores[f.recipe_id] = (scores[f.recipe_id] || 1) - 1;
  });

  return scores;
}

function hasData(recipeId, swipes, feedback, memberIds) {
  for (const mid of memberIds) {
    if (swipes[mid]?.[recipeId] !== undefined) return true;
  }
  if (feedback?.some(f => f.recipe_id === recipeId && memberIds.includes(f.member_id))) return true;
  return false;
}

function avgStars(recipeId, feedback, memberIds) {
  const idSet = new Set(memberIds);
  const ratings = (feedback || []).filter(f => f.recipe_id === recipeId && idSet.has(f.member_id));
  if (ratings.length === 0) return null;
  return ratings.reduce((sum, f) => sum + f.stars, 0) / ratings.length;
}

function ScoreBar({ score, maxScore }) {
  const pct = maxScore > 0 ? Math.max(0, Math.min(100, (score / maxScore) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="flex-1 h-2 lg:h-2.5 bg-surface rounded-full overflow-hidden">
        <div
          className="h-full bg-lime rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-body text-xs font-semibold text-ink-soft w-6 text-right">{score}</span>
    </div>
  );
}

function StarRating({ avg }) {
  if (avg === null) return null;
  return (
    <span className="font-body text-xs text-ink-soft flex items-center gap-0.5">
      {avg.toFixed(1)} ★
    </span>
  );
}

function RecipeRow({ rank, recipe, score, maxScore, avg }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="font-body text-sm font-medium text-ink-muted w-6 text-right tabular-nums">{rank}</span>
      <RecipeThumb recipe={recipe} className="w-9 h-9 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-body text-sm font-medium text-ink truncate">{recipe.name}</span>
          <StarRating avg={avg} />
        </div>
        <ScoreBar score={score} maxScore={maxScore} />
      </div>
    </div>
  );
}

function FavoriteRow({ recipe, avg, onRemove }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <RecipeThumb recipe={recipe} className="w-9 h-9 shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="font-body text-sm font-medium text-ink truncate">{recipe.name}</span>
        <StarRating avg={avg} />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${recipe.name} from favorites`}
        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-ink-muted hover:text-red-500 hover:bg-surface transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function MemberStandingRow({ rank, member, swipeCount }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <span className="font-body text-base font-medium text-ink-muted w-7 text-center tabular-nums">{rank}</span>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime/20 to-lime/20 flex items-center justify-center text-lg ring-2 ring-lime/30 overflow-hidden"><Avatar url={member.avatar_url} fallback={member.avatar} name={member.name} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="font-body text-sm font-semibold text-ink truncate">{member.name.split(' ')[0]}</p>
        </div>
        <p className="font-body text-[11px] text-ink-muted">{swipeCount} rated</p>
      </div>
    </div>
  );
}

const MEAL_FILTERS = [
  { id: null, label: 'All' },
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'dinner', label: 'Dinner' },
];

export default function StatsScreen() {
  const { householdId, currentMember } = useAuth();
  const { recipes, loading: recipesLoading, error: recipesError, refetch: recipesRefetch } = useRecipes(householdId);
  const { swipes, loading: swipesLoading, error: swipesError, refetch: swipesRefetch } = useSwipes(householdId);
  const { members, loading: membersLoading, error: membersError, refetch: membersRefetch } = useFamilyMembers(householdId);
  const { feedback, loading: feedbackLoading, error: feedbackError, refetch: feedbackRefetch } = useAllFeedback(householdId);
  const { favorites: favoriteRecords, toggleFavorite } = useFavorites(householdId);

  const [selectedMemberId, setSelectedMemberId] = useState(null); // null = everyone
  const [selectedMeal, setSelectedMeal] = useState(null); // null = all meals

  const loading = recipesLoading || swipesLoading || membersLoading || feedbackLoading;

  const allMemberIds = useMemo(() => members.map(m => m.id), [members]);
  const activeMemberIds = useMemo(
    () => selectedMemberId ? [selectedMemberId] : allMemberIds,
    [selectedMemberId, allMemberIds]
  );

  const recipeMap = useMemo(() => {
    const map = {};
    recipes.forEach(r => { map[r.id] = r; });
    return map;
  }, [recipes]);

  // Filter recipes by meal category
  const filteredRecipes = useMemo(
    () => selectedMeal ? recipes.filter(r => r.category === selectedMeal) : recipes,
    [recipes, selectedMeal]
  );

  // Scores scoped to active filters
  const scores = useMemo(
    () => computeScores(filteredRecipes, swipes, feedback, activeMemberIds),
    [filteredRecipes, swipes, feedback, activeMemberIds]
  );

  // Top 5 favorites
  const favorites = useMemo(() => {
    return Object.entries(scores)
      .filter(([rid]) => recipeMap[rid])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [scores, recipeMap]);

  // Curated "cook again" favorites (explicit, household-level), newest first,
  // respecting the meal filter but not the per-member filter.
  const favoriteRecipes = useMemo(() => {
    return (favoriteRecords || [])
      .map(f => ({ recipe: recipeMap[f.recipe_id], markedAt: f.marked_at }))
      .filter(x => x.recipe && (!selectedMeal || x.recipe.category === selectedMeal))
      .sort((a, b) => (b.markedAt || '').localeCompare(a.markedAt || ''));
  }, [favoriteRecords, recipeMap, selectedMeal]);

  // Bottom 5 least loved (only rated recipes), excluding anything already shown
  // in Top picks — otherwise, with few scored recipes, the top-5 and bottom-5
  // sets overlap and the same recipe appears in both lists.
  const leastLoved = useMemo(() => {
    const topIds = new Set(favorites.map(([rid]) => rid));
    return Object.entries(scores)
      .filter(([rid]) => recipeMap[rid] && !topIds.has(rid) && hasData(rid, swipes, feedback, activeMemberIds))
      .sort((a, b) => a[1] - b[1])
      .slice(0, 5);
  }, [scores, swipes, feedback, activeMemberIds, recipeMap, favorites]);

  const maxScore = favorites.length > 0 ? favorites[0][1] : 1;

  const memberStandings = useMemo(() => {
    return members.map(m => {
      const swipeCount = Object.keys(swipes[m.id] || {}).length;
      return { member: m, swipeCount };
    }).sort((a, b) => b.swipeCount - a.swipeCount);
  }, [members, swipes]);

  const noData = allMemberIds.length > 0 &&
    !allMemberIds.some(mid => Object.keys(swipes[mid] || {}).length > 0) &&
    (!feedback || feedback.length === 0) &&
    (favoriteRecords?.length || 0) === 0;

  const error = recipesError || swipesError || membersError || feedbackError;
  const refetchAll = () => { recipesRefetch(); swipesRefetch(); membersRefetch(); feedbackRefetch(); };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} onRetry={refetchAll} />;

  if (noData) {
    return (
      <div>
        <h2 className="font-display text-xl lg:text-2xl font-semibold text-ink mb-6">Stats</h2>
        <EmptyState title="Nothing rated yet" description="Rate a few dinners or leave ratings to see your family's favorites" action="Rate dinners" to="/swipe" />
      </div>
    );
  }

  const _filterLabel = [
    selectedMemberId ? members.find(m => m.id === selectedMemberId)?.name : 'Everyone',
    selectedMeal ? selectedMeal : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="space-y-5 lg:space-y-6">
      <h2 className="font-display text-xl lg:text-2xl font-semibold text-ink">Stats</h2>

      {/* Member filter - horizontal scroll on mobile, visible row on desktop */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 lg:overflow-visible lg:mx-0 lg:px-0 lg:flex-wrap" style={{ scrollbarWidth: 'none' }}>
        <button
          type="button"
          onClick={() => setSelectedMemberId(null)}
          className={`shrink-0 px-3.5 py-2 rounded-xl font-body text-sm font-semibold transition-all duration-200 ${
            selectedMemberId === null
              ? 'bg-surface text-ink shadow-sm'
              : 'bg-panel border border-hairline text-ink-soft hover:border-hairline'
          }`}
        >
          Everyone
        </button>
        {members.map(m => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSelectedMemberId(m.id)}
            className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-body text-sm font-semibold transition-all duration-200 ${
              m.id === selectedMemberId
                ? 'bg-surface text-ink shadow-sm'
                : 'bg-panel border border-hairline text-ink-soft hover:border-hairline'
            }`}
          >
            <span className="w-5 h-5 rounded-full overflow-hidden inline-flex items-center justify-center text-base shrink-0"><Avatar url={m.avatar_url} fallback={m.avatar} name={m.name} /></span>
            {m.name.split(' ')[0]}
          </button>
        ))}
      </div>

      {/* Meal filter */}
      <div className="flex bg-surface rounded-xl p-1 gap-1">
        {MEAL_FILTERS.map(f => (
          <button
            key={f.id || 'all'}
            type="button"
            onClick={() => setSelectedMeal(f.id)}
            className={`flex-1 py-2 rounded-lg font-body text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1 ${
              selectedMeal === f.id
                ? 'bg-panel text-ink shadow-sm shadow-glow-sm'
                : 'text-ink-soft hover:text-ink-soft'
            }`}
          >
            {f.id && <MealIcon meal={f.id} className="w-4 h-4" />}
            {f.label}
          </button>
        ))}
      </div>

      {selectedMemberId === null && memberStandings.length > 0 && (
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
          <h3 className="font-display font-semibold text-ink mb-1 lg:text-lg">Family activity</h3>
          <p className="font-body text-xs text-ink-muted mb-4">By meals rated</p>
          <div className="divide-y divide-hairline">
            {memberStandings.map((s, i) => (
              <MemberStandingRow key={s.member.id} rank={i + 1} member={s.member} swipeCount={s.swipeCount} />
            ))}
          </div>
        </div>
      )}

      {/* Family favorites — explicit "cook again" list, distinct from Top picks */}
      <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">⭐</span>
          <h3 className="font-display font-semibold text-ink lg:text-lg">Family favorites</h3>
        </div>
        <p className="font-body text-xs text-ink-muted mb-3">Meals your family marked &ldquo;we&apos;d cook this again&rdquo;</p>
        {favoriteRecipes.length === 0 ? (
          <p className="font-body text-sm text-ink-muted">No favorites yet. Mark one from the post-meal rating on a meal.</p>
        ) : (
          <div className="divide-y divide-hairline">
            {favoriteRecipes.map(({ recipe }) => (
              <FavoriteRow
                key={recipe.id}
                recipe={recipe}
                avg={avgStars(recipe.id, feedback, allMemberIds)}
                onRemove={() => toggleFavorite(recipe.id, currentMember?.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Favorites & Least Loved */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-5 lg:space-y-0">
        {/* Favorites */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
          <h3 className="font-display font-semibold text-ink mb-3 lg:text-lg">Top picks</h3>
          {favorites.length === 0 ? (
            <p className="font-body text-sm text-ink-muted">No recipes scored yet</p>
          ) : (
            <div className="divide-y divide-hairline">
              {favorites.map(([rid, score], i) => (
                <RecipeRow
                  key={rid}
                  rank={i + 1}
                  recipe={recipeMap[rid]}
                  score={score}
                  maxScore={maxScore}
                  avg={avgStars(rid, feedback, activeMemberIds)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Least Loved */}
        <div className="bg-panel rounded-2xl p-5 lg:p-6 border border-hairline">
          <h3 className="font-display font-semibold text-ink mb-3 lg:text-lg">Passed On</h3>
          {leastLoved.length === 0 ? (
            <p className="font-body text-sm text-ink-muted">No rated recipes yet</p>
          ) : (
            <div className="divide-y divide-hairline">
              {leastLoved.map(([rid, score], i) => (
                <RecipeRow
                  key={rid}
                  rank={i + 1}
                  recipe={recipeMap[rid]}
                  score={score}
                  maxScore={maxScore}
                  avg={avgStars(rid, feedback, activeMemberIds)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
